/*

& "Discard changes?" when a listing section's edit is closed with unsaved changes (3 Nov 2026, the app's listing
& screen): "Yes, cancel" drops the edits, "Save changes" saves them straight away (the dialog carries the review note,
& so the save doesn't ask "Send for review?" again), and closing the dialog keeps editing.

  One dialog, mounted once on the listing page (<SectionDiscardConfirmHost />). A section asks it from its close X with
  `confirmDiscardChanges()` when `useEditSnapshot(isEditing)()` says something changed. Without a host it answers
  "discard" at once, so the X behaves as before.

*/

import { Dialog, DialogContent, Typography } from "@mui/material";
import { useRouter } from "next/router";
import React from "react";
import { useSelector } from "react-redux";

import BeegruButton from "../../common-components/buttons/BeegruButton";
import StateInterface from "../../../redux-magic/state-interface";

export type DiscardAnswer = "save" | "discard" | "keep";

let openHost: ((resolve: (answer: DiscardAnswer) => void) => void) | null = null;

/** Asks "Discard changes?"; resolves "save", "discard" or "keep" (keep editing). */
export function confirmDiscardChanges(): Promise<DiscardAnswer> {
	if (!openHost) return Promise.resolve("discard");
	const opener = openHost;
	return new Promise((resolve) => opener(resolve));
}

/**
 * Whether the listing changed since [isEditing] turned on. The sections edit the page's listing in the store as the
 * owner types (and put the copy back on close), so a snapshot of it taken when editing starts tells.
 */
export function useEditSnapshot(isEditing: boolean): () => boolean {
	const property = useSelector((state: { globalState: StateInterface }) => state.globalState?.single_property_page?.property);
	const snapshot = React.useRef<string>("");
	const latest = React.useRef<string>("");
	latest.current = JSON.stringify(property ?? null);
	React.useEffect(() => {
		snapshot.current = isEditing ? latest.current : "";
		// Taken once, when editing starts.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isEditing]);
	return () => Boolean(snapshot.current) && snapshot.current !== latest.current;
}

export const SectionDiscardConfirmHost = () => {
	const router = useRouter();
	const tr = (en: string, ar: string, kn: string): string =>
		router.locale === "ar-AE" ? ar : router.locale === "kn-IN" ? kn : en;

	const [pending, setPending] = React.useState<((answer: DiscardAnswer) => void) | null>(null);

	React.useEffect(() => {
		openHost = (resolve) => setPending(() => resolve);
		return () => {
			openHost = null;
		};
	}, []);

	const answer = (value: DiscardAnswer) => {
		pending?.(value);
		setPending(null);
	};

	return (
		<Dialog
			open={Boolean(pending)}
			onClose={() => answer("keep")}
			PaperProps={{ sx: { borderRadius: "1rem", maxWidth: "24rem" } }}
		>
			<DialogContent sx={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
				<Typography variant="h6">{tr("Discard changes?", "تجاهل التغييرات؟", "ಬದಲಾವಣೆಗಳನ್ನು ತಿರಸ್ಕರಿಸುವುದೇ?")}</Typography>
				<Typography sx={{ fontSize: "0.875rem", color: "#424242" }}>
					{tr(
						"You have unsaved changes. If you cancel now, your edits to this property will be lost. Saving them sends the listing back for review, and it stays unlisted until it's approved.",
						"لديك تغييرات غير محفوظة. إذا ألغيت الآن، ستفقد تعديلاتك على هذا العقار. حفظها يعيد العقار إلى المراجعة، ويبقى غير معروض حتى تتم الموافقة عليه.",
						"ನೀವು ಉಳಿಸದ ಬದಲಾವಣೆಗಳನ್ನು ಹೊಂದಿದ್ದೀರಿ. ಈಗ ರದ್ದುಗೊಳಿಸಿದರೆ ಈ ಆಸ್ತಿಗೆ ಮಾಡಿದ ಬದಲಾವಣೆಗಳು ಕಳೆದುಹೋಗುತ್ತವೆ. ಉಳಿಸಿದರೆ ಪಟ್ಟಿ ಮತ್ತೆ ಪರಿಶೀಲನೆಗೆ ಹೋಗುತ್ತದೆ ಮತ್ತು ಅನುಮೋದನೆಯವರೆಗೆ ಕಾಣಿಸುವುದಿಲ್ಲ.",
					)}
				</Typography>
				<div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem" }}>
					<BeegruButton
						variant="outlined"
						flavor="error"
						size="small"
						onClick={() => answer("discard")}
					>
						{tr("Yes, cancel", "نعم، إلغاء", "ಹೌದು, ರದ್ದುಮಾಡಿ")}
					</BeegruButton>
					<BeegruButton
						variant="contained"
						flavor="primary"
						size="small"
						onClick={() => answer("save")}
					>
						{tr("Save changes", "حفظ التغييرات", "ಬದಲಾವಣೆಗಳನ್ನು ಉಳಿಸಿ")}
					</BeegruButton>
				</div>
			</DialogContent>
		</Dialog>
	);
};
